import { create } from 'zustand';
import { io, type Socket } from 'socket.io-client';
import type {
  ClientToServerEvents,
  ServerToClientEvents,
} from '@glowearth/shared/types';

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL ?? window.location.origin;

export interface SocketState {
  socket: Socket<ServerToClientEvents, ClientToServerEvents> | null;
  connected: boolean;
  currentRoom: string | null;

  connect: (token: string) => void;
  joinRoom: (h3Res3Index: string) => void;
  leaveRoom: (h3Res3Index: string) => void;
  disconnect: () => void;
}

export const useSocketStore = create<SocketState>()((set, get) => ({
  socket: null,
  connected: false,
  currentRoom: null,

  connect: (token) => {
    const existing = get().socket;
    if (existing?.connected) {
      return;
    }

    const socket: Socket<ServerToClientEvents, ClientToServerEvents> = io(
      SOCKET_URL,
      {
        auth: { token },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      }
    );

    socket.on('connect', () => {
      set({ connected: true });
    });

    socket.on('disconnect', () => {
      set({ connected: false, currentRoom: null });
    });

    socket.on('connect_error', (err) => {
      console.error('Socket connect error:', err.message);
    });

    set({ socket });
  },

  joinRoom: (h3Res3Index) => {
    const { socket, currentRoom } = get();
    if (!socket?.connected) {
      return;
    }

    if (currentRoom && currentRoom !== h3Res3Index) {
      socket.emit('room:leave', currentRoom);
    }

    socket.emit('room:join', h3Res3Index);
    set({ currentRoom: h3Res3Index });
  },

  leaveRoom: (h3Res3Index) => {
    const { socket, currentRoom } = get();
    if (!socket?.connected) {
      return;
    }

    socket.emit('room:leave', h3Res3Index);
    if (currentRoom === h3Res3Index) {
      set({ currentRoom: null });
    }
  },

  disconnect: () => {
    const { socket } = get();
    if (socket) {
      socket.disconnect();
      socket.removeAllListeners();
    }
    set({ socket: null, connected: false, currentRoom: null });
  },
}));
